/**
 * AppLayout — Shared layout wrapper providing consistent sidebar (desktop)
 * and mobile header across all pages. Ensures responsive UI consistency.
 *
 * Sidebar structure:
 * - Logo (25% larger)
 * - Battalion on duty indicator
 * - Navigation links (main, officer, admin)
 * - Admin Settings link (admin only, above user profile)
 * - Theme toggle
 * - User profile pane at bottom (avatar, name, title, dept)
 *   - Help/Guide link
 *   - Sign Out
 */

import { useState, useRef, useEffect, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { isOfficerOrAbove, isAdmin as isAdminRole } from '../../lib/auth';
import { computeBattalionForDate, getTodayDateStr } from '../../lib/schedule';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import ThemeToggle from '../ThemeToggle';
import FeedbackButton from '../FeedbackButton';
import OfflineIndicator from '../OfflineIndicator';
import NotificationBell from '../NotificationBell';
import { useInstallPrompt } from '../../hooks/useInstallPrompt';
import {
  Home,
  Clock,
  Calendar,
  Users,
  ArrowLeftRight,
  HelpCircle,
  FileText,
  Settings,
  Menu,
  X,
  LogOut,
  User,
  ClipboardList,
  Download,
  Share,
  LayoutGrid,
} from 'lucide-react';

interface AppLayoutProps {
  children: ReactNode;
  /** Page title shown in the mobile header */
  title?: string;
}

// Colors match printed 2026 Duty Calendar
const BATTALION_TEXT_COLORS: Record<number, string> = {
  1: '#4DD0E1', // Cyan
  2: '#AED581', // Lime Green
  3: '#EF9A9A', // Red
  4: '#FFB74D', // Orange
  5: '#9FA8DA', // Indigo
};

interface NavItem {
  label: string;
  path: string;
  icon: typeof Home;
}

export default function AppLayout({ children, title }: AppLayoutProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  // Client-side battalion computation is the authoritative source — guarantees
  // correctness even when pre-generated DB schedule data is stale or missing.
  const todayComputed = computeBattalionForDate(getTodayDateStr());
  const onDutyBattalionNumber = todayComputed.battalion;
  const [showMenu, setShowMenu] = useState(false);
  const [showUserPopup, setShowUserPopup] = useState(false);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const userPopupRef = useRef<HTMLDivElement>(null);
  const { isPwa, canInstall, isIos, triggerInstall } = useInstallPrompt();

  // Attempt to load profile photo from Microsoft Graph via our proxy
  useEffect(() => {
    if (!user) return;
    const controller = new AbortController();
    fetch('/api/auth/photo', { credentials: 'include', signal: controller.signal })
      .then((res) => {
        if (res.ok) return res.blob();
        return null;
      })
      .then((blob) => {
        if (blob) setPhotoUrl(URL.createObjectURL(blob));
      })
      .catch(() => {
        /* Photo loading is best-effort */
      });
    return () => controller.abort();
  }, [user]);

  // Close user popup on outside click
  useEffect(() => {
    if (!showUserPopup) return;
    function handleClickOutside(e: MouseEvent) {
      if (userPopupRef.current && !userPopupRef.current.contains(e.target as Node)) {
        setShowUserPopup(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserPopup]);

  // Update browser tab title
  useEffect(() => {
    document.title = title ? `ShiftHappens\u2122 | ${title}` : 'ShiftHappens\u2122 | Home';
  }, [title]);

  const userRole = user?.role ?? 'member';
  const isOfficerOrAdmin = isOfficerOrAbove(user);
  const isAdmin = isAdminRole(user);

  const navItems: NavItem[] = [
    { label: 'Home', path: '/', icon: Home },
    { label: 'My Hours', path: '/history', icon: Clock },
    { label: 'Duty Schedule', path: '/schedule', icon: Calendar },
    { label: "Who's On Duty", path: '/on-shift', icon: Users },
    { label: 'Shift Planning', path: '/shift-planning', icon: LayoutGrid },
    { label: 'Roster', path: '/roster', icon: ClipboardList },
    { label: 'Swaps & Coverage', path: '/swaps', icon: ArrowLeftRight },
  ];

  const officerItems: NavItem[] = [{ label: 'Reports', path: '/reports', icon: FileText }];

  const adminItems: NavItem[] = [{ label: 'Settings', path: '/admin/settings', icon: Settings }];

  // Desktop Layout
  if (isDesktop) {
    return (
      <div className="flex min-h-dvh bg-surface-base">
        {/* Aurora background */}
        <div className="aurora-bg" aria-hidden="true" />

        {/* Sidebar */}
        <nav
          className="w-[240px] min-h-dvh bg-surface-raised/80 glass-panel-elevated
                     border-r border-edge-muted fixed top-0 left-0 bottom-0 z-50
                     flex flex-col"
          aria-label="Main navigation"
        >
          {/* Logo — 25% larger */}
          <div className="px-4 pt-5 pb-4 flex justify-center">
            <img src="/shifthappens-logo-dark.png" alt="ShiftHappens" className="h-9 w-auto dark:block hidden" />
            <img src="/shifthappens-logo-dark.png" alt="ShiftHappens" className="h-9 w-auto dark:hidden block" />
          </div>

          {/* Battalion on duty — always shown via client-side computation */}
          <div
            className="px-4 pb-3 text-xs font-bold uppercase tracking-wider text-center"
            style={{
              color: BATTALION_TEXT_COLORS[onDutyBattalionNumber] ?? 'var(--interactive-primary)',
            }}
          >
            <span className="text-content-tertiary">On Duty:</span> Battalion {onDutyBattalionNumber}
          </div>

          {/* Nav links */}
          <div className="flex-1 overflow-y-auto px-2">
            <div className="flex flex-col gap-0.5">
              {navItems.map((item) => (
                <SidebarLink
                  key={item.path}
                  item={item}
                  active={location.pathname === item.path}
                  onClick={() => navigate(item.path)}
                />
              ))}
            </div>

            {/* Officer section */}
            {isOfficerOrAdmin && (
              <>
                <SectionLabel>Officer</SectionLabel>
                <div className="flex flex-col gap-0.5">
                  {officerItems.map((item) => (
                    <SidebarLink
                      key={item.path}
                      item={item}
                      active={location.pathname === item.path}
                      onClick={() => navigate(item.path)}
                    />
                  ))}
                </div>
              </>
            )}

            {/* Admin section */}
            {isAdmin && (
              <>
                <SectionLabel>Admin</SectionLabel>
                <div className="flex flex-col gap-0.5">
                  {adminItems.map((item) => (
                    <SidebarLink
                      key={item.path}
                      item={item}
                      active={location.pathname === item.path}
                      onClick={() => navigate(item.path)}
                    />
                  ))}
                </div>
              </>
            )}

            {/* Feedback */}
            <div className="mt-4 px-1">
              <FeedbackButton variant="sidebar" />
            </div>

            {/* Install PWA link — hidden when already installed */}
            {!isPwa && (canInstall || isIos) && (
              <div className="mt-2 px-1">
                {canInstall ? (
                  <button
                    type="button"
                    onClick={triggerInstall}
                    className="flex items-center gap-2.5 w-full px-3 py-2 text-sm rounded-lg
                      text-action-primary hover:bg-action-primary/10 transition-colors duration-150
                      cursor-pointer text-left border-none bg-transparent"
                  >
                    <Download className="h-4 w-4 shrink-0" />
                    Install App
                  </button>
                ) : (
                  <div className="flex items-center gap-2.5 px-3 py-2 text-sm text-content-secondary">
                    <Share className="h-4 w-4 shrink-0" />
                    <span className="text-xs">
                      Tap <strong>Share</strong> then <strong>Add to Home Screen</strong>
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Bottom section: Theme, Notifications, User profile */}
          <div className="border-t border-edge-muted">
            {/* Theme toggle + Notification bell */}
            <div className="flex items-center justify-center gap-2 py-2">
              <ThemeToggle />
              <NotificationBell />
            </div>

            {/* User profile — clickable, opens popup */}
            <div className="px-3 pb-3 relative" ref={userPopupRef}>
              <button
                type="button"
                onClick={() => setShowUserPopup(!showUserPopup)}
                className="w-full bg-surface-sunken rounded-lg p-2.5 flex items-center gap-2.5
                           cursor-pointer hover:bg-surface-base transition-colors duration-150 text-left border-none"
              >
                {photoUrl ? (
                  <img
                    src={photoUrl}
                    alt=""
                    className="w-8 h-8 rounded-full object-cover border border-edge-muted shrink-0"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-action-primary/20 border border-edge-muted flex items-center justify-center shrink-0">
                    <User className="h-4 w-4 text-action-primary" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-content-primary truncate">{user?.name ?? ''}</div>
                  <div className="text-[10px] text-content-tertiary truncate capitalize">{userRole}</div>
                </div>
              </button>

              {/* User popup modal */}
              {showUserPopup && (
                <div
                  className="absolute bottom-full left-3 right-3 mb-2 bg-surface-raised border border-edge-default
                             rounded-xl shadow-xl z-[60] p-3 space-y-1"
                >
                  <button
                    type="button"
                    onClick={() => {
                      setShowUserPopup(false);
                      navigate('/profile');
                    }}
                    className={`flex items-center gap-2.5 w-full px-3 py-2 text-[13px] rounded-lg
                      transition-colors duration-150 cursor-pointer text-left border-none
                      ${
                        location.pathname === '/profile'
                          ? 'text-action-primary font-semibold bg-action-primary/10'
                          : 'text-content-primary bg-transparent hover:bg-surface-sunken'
                      }`}
                  >
                    <User className="h-4 w-4 shrink-0" />
                    Profile
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setShowUserPopup(false);
                      navigate('/guide');
                    }}
                    className={`flex items-center gap-2.5 w-full px-3 py-2 text-[13px] rounded-lg
                      transition-colors duration-150 cursor-pointer text-left border-none
                      ${
                        location.pathname === '/guide'
                          ? 'text-action-primary font-semibold bg-action-primary/10'
                          : 'text-content-primary bg-transparent hover:bg-surface-sunken'
                      }`}
                  >
                    <HelpCircle className="h-4 w-4 shrink-0" />
                    Help / Guide
                  </button>

                  <div className="border-t border-edge-muted my-1" />

                  <button
                    type="button"
                    onClick={() => {
                      setShowUserPopup(false);
                      logout();
                    }}
                    className="flex items-center gap-2.5 w-full px-3 py-2 text-[13px] text-danger-400
                             rounded-lg cursor-pointer hover:bg-danger-500/10 bg-transparent
                             transition-colors duration-150 text-left border-none"
                  >
                    <LogOut className="h-4 w-4 shrink-0" />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </nav>

        {/* Main content */}
        <main className="flex-1 ml-[240px] relative">
          <OfflineIndicator />
          <div className="px-6 lg:px-10 pb-10 pt-2 safe-bottom">{children}</div>
        </main>
      </div>
    );
  }

  // Mobile Layout
  return (
    <div className="min-h-dvh bg-surface-base relative">
      {/* Aurora background */}
      <div className="aurora-bg" aria-hidden="true" />

      {/* Mobile header */}
      <header
        className="sticky top-0 z-50 backdrop-blur-xl safe-top"
        style={{ backgroundColor: 'color-mix(in oklch, var(--surface-base) 92%, transparent)' }}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-edge-muted">
          <div className="flex items-center gap-3">
            <img src="/shifthappens-logo-dark.png" alt="ShiftHappens" className="h-8 w-auto" />
            {title && <span className="text-sm font-semibold text-content-primary">{title}</span>}
          </div>

          <div className="flex items-center gap-2">
            <span
              className="text-xs font-bold uppercase tracking-wider"
              style={{
                color: BATTALION_TEXT_COLORS[onDutyBattalionNumber] ?? 'var(--interactive-primary)',
              }}
            >
              <span className="text-content-tertiary">On Duty:</span> Bn {onDutyBattalionNumber}
            </span>
            <NotificationBell />
            <button
              type="button"
              onClick={() => setShowMenu(!showMenu)}
              aria-label={showMenu ? 'Close menu' : 'Open menu'}
              aria-expanded={showMenu}
              className="p-2 rounded-lg text-content-secondary hover:bg-surface-sunken
                         min-h-11 min-w-11 flex items-center justify-center cursor-pointer
                         transition-colors duration-150"
            >
              {showMenu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile slide-down menu */}
        {showMenu && (
          <div
            className="absolute left-0 right-0 top-full backdrop-blur-xl
                       border-b border-edge-default max-h-[80dvh] overflow-y-auto z-50"
            style={{ backgroundColor: 'color-mix(in oklch, var(--surface-base) 94%, transparent)' }}
          >
            {/* Nav items */}
            <div className="py-2">
              {navItems.map((item) => (
                <MobileMenuLink
                  key={item.path}
                  item={item}
                  active={location.pathname === item.path}
                  onClick={() => {
                    setShowMenu(false);
                    navigate(item.path);
                  }}
                />
              ))}

              {/* Officer items */}
              {isOfficerOrAdmin && (
                <>
                  <div className="px-4 pt-3 pb-1 text-[11px] font-bold text-content-tertiary uppercase tracking-widest">
                    Officer
                  </div>
                  {officerItems.map((item) => (
                    <MobileMenuLink
                      key={item.path}
                      item={item}
                      active={location.pathname === item.path}
                      onClick={() => {
                        setShowMenu(false);
                        navigate(item.path);
                      }}
                    />
                  ))}
                </>
              )}

              {/* Admin items */}
              {isAdmin && (
                <>
                  <div className="px-4 pt-3 pb-1 text-[11px] font-bold text-content-tertiary uppercase tracking-widest">
                    Admin
                  </div>
                  {adminItems.map((item) => (
                    <MobileMenuLink
                      key={item.path}
                      item={item}
                      active={location.pathname === item.path}
                      onClick={() => {
                        setShowMenu(false);
                        navigate(item.path);
                      }}
                    />
                  ))}
                </>
              )}

              {/* Feedback */}
              <div className="px-4 pt-3">
                <FeedbackButton variant="sidebar" />
              </div>
            </div>

            {/* User section */}
            <div className="px-4 py-3 border-t border-edge-muted">
              <div className="flex items-center gap-3 mb-3">
                {photoUrl ? (
                  <img
                    src={photoUrl}
                    alt=""
                    className="w-9 h-9 rounded-full object-cover border border-edge-muted shrink-0"
                  />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-action-primary/20 border border-edge-muted flex items-center justify-center shrink-0">
                    <User className="h-4 w-4 text-action-primary" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-content-primary truncate">{user?.name ?? ''}</div>
                  <div className="text-xs text-content-tertiary truncate capitalize">{userRole}</div>
                </div>
                <ThemeToggle />
              </div>

              <div className="flex flex-col gap-0.5 mb-3">
                <MobileMenuLink
                  item={{ label: 'Profile', path: '/profile', icon: User }}
                  active={location.pathname === '/profile'}
                  onClick={() => {
                    setShowMenu(false);
                    navigate('/profile');
                  }}
                />
                <MobileMenuLink
                  item={{ label: 'Help / Guide', path: '/guide', icon: HelpCircle }}
                  active={location.pathname === '/guide'}
                  onClick={() => {
                    setShowMenu(false);
                    navigate('/guide');
                  }}
                />
              </div>

              {/* Install PWA link — hidden when already installed */}
              {!isPwa && (canInstall || isIos) && (
                <div className="mb-3">
                  {canInstall ? (
                    <button
                      type="button"
                      onClick={async () => {
                        await triggerInstall();
                        setShowMenu(false);
                      }}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium
                                 text-action-primary bg-action-primary/10 border border-action-primary/30
                                 rounded-lg cursor-pointer hover:bg-action-primary/20
                                 transition-colors duration-150"
                    >
                      <Download className="h-4 w-4" />
                      Install App
                    </button>
                  ) : (
                    <div className="flex items-center gap-2 px-4 py-2.5 text-xs text-content-secondary">
                      <Share className="h-4 w-4 shrink-0" />
                      <span>
                        Tap <strong>Share</strong> then <strong>Add to Home Screen</strong> to install
                      </span>
                    </div>
                  )}
                </div>
              )}

              <button
                type="button"
                onClick={() => {
                  setShowMenu(false);
                  logout();
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium
                           text-content-secondary bg-surface-sunken border border-edge-default
                           rounded-lg cursor-pointer hover:bg-action-secondary-hover
                           transition-colors duration-150"
              >
                <LogOut className="h-4 w-4" />
                Sign Out
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Mobile menu backdrop blur overlay — must be outside <header> to avoid
          backdrop-filter creating a containing block that traps the fixed overlay */}
      {showMenu && (
        <div
          className="fixed inset-0 z-40 backdrop-blur-sm bg-black/30"
          onClick={() => setShowMenu(false)}
          aria-hidden="true"
        />
      )}

      <OfflineIndicator />

      {/* Main content */}
      <main className="relative px-4 pb-10 pt-2 safe-bottom">{children}</main>
    </div>
  );
}

// -- Sub-components --

function SidebarLink({ item, active, onClick }: { item: NavItem; active: boolean; onClick: () => void }) {
  const Icon = item.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        flex items-center gap-2.5 w-full px-3 py-2 text-sm rounded-lg
        transition-colors duration-150 cursor-pointer text-left
        ${
          active
            ? 'bg-action-primary/10 text-action-primary font-semibold'
            : 'text-content-secondary hover:bg-surface-sunken hover:text-content-primary'
        }
      `}
    >
      <Icon className="h-4 w-4 shrink-0" />
      {item.label}
    </button>
  );
}

function MobileMenuLink({ item, active, onClick }: { item: NavItem; active: boolean; onClick: () => void }) {
  const Icon = item.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        flex items-center gap-3 w-full px-4 py-2.5 text-sm
        transition-colors duration-150 cursor-pointer text-left
        ${
          active
            ? 'text-action-primary font-semibold bg-action-primary/5'
            : 'text-content-primary hover:bg-surface-sunken'
        }
      `}
    >
      <Icon className="h-4 w-4 shrink-0" />
      {item.label}
    </button>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="px-3 pt-4 pb-1 text-[11px] font-bold text-content-tertiary uppercase tracking-widest">
      {children}
    </div>
  );
}
